import React, { Component, createRef, Dispatch } from 'react';
import { connect } from 'react-redux';
import { AppState } from 'store';
import {
    AbstractSofaForm,
    getPropsForSofaInput,
    maxLengthFilter,
    NOT_NUMBER_REGEXP,
    SofaButton,
    SofaForm,
    SofaFormControl,
    SofaFormControlDescription,
    SofaInput,
    SofaSlider,
    textFilter
} from '@sofology/sofologycomponents';

import './BasketPaymentTypeSelector.scss';

import { NotificationTypes } from 'models';
import { BasketAddressModel, BasketDetailsModel, UpsellTypes } from 'models/Basket';
import {
    PaymentTypes,
    PaymentDetailsModel,
    CardPaymentStateModel,
    IfcPaymentStateModel,
} from 'models/Payment';
import {
    changePaymentType,
    ChangePaymentTypeFunc,
    updateCardPayment,
    UpdateCardPaymentFunc,
    UpdateIfcPaymentFunc,
    updateIfcPayment,
} from 'store/payment/actions';
import { generateDispatchToPropsMap, ifcPaymentHelper, cardPaymentHelper, scrollHelper, errorToBasketErrorModel, logsHelper } from 'helpers';
import { formatCurrency } from 'formatters';
import { Loader } from 'components';
import { addNotification, AddNotificationFunc } from 'store/notifications/actions';
import { Validatable, Scrollable } from 'interfaces';
import { BasketLoadingItem } from 'store/basket';

const PAYMENT_ERROR_MESSAGE = 'Please select Payment Method';
const IFC_INPUT_MAX_LENGTH = 5;

interface OwnProps {
    ref?: React.RefObject<BasketPaymentTypeSelectorType>;
}

interface StateProps {
    isPaymentCalculatorInitializing: boolean;
    paymentDetails: PaymentDetailsModel;
    updatedCardPaymentState: CardPaymentStateModel | null;
    updatedIfcPaymentState: IfcPaymentStateModel | null;
    totalPrice: number;
    isPaymentUpdating: boolean;
    isPaymentTypeUpdating: boolean;
    deliveryAddress: BasketAddressModel | null;
    isIfcFormSubmitted: boolean;
    basket: BasketDetailsModel;
    basketLoadingItems: BasketLoadingItem[];
    basketLoadingUpsells: UpsellTypes[];
    isDonationLoading: boolean;
    isCalculatingDeliveryCost: boolean;
}

interface DispatchProps {
    changePaymentType: ChangePaymentTypeFunc;
    updateCardPayment: UpdateCardPaymentFunc;
    updateIfcPayment: UpdateIfcPaymentFunc;
    addNotification: AddNotificationFunc;
}

interface OwnState {
    cardPaymentState: CardPaymentStateModel | null;
    ifcPaymentState: IfcPaymentStateModel | null;
    isSliderMoving: boolean;
    isValid?: boolean;
    ifcCalculatorForm: SofaForm<IfcCalculatorFormControls>;
}

interface IfcCalculatorFormControls extends AbstractSofaForm {
    deposit: SofaFormControl<string>;
    months: SofaFormControl<string>;
}

class BasketPaymentTypeSelector extends Component<OwnProps & StateProps & DispatchProps, OwnState> implements Validatable, Scrollable {
    private element = React.createRef<HTMLDivElement>();

    constructor(props: OwnProps & StateProps & DispatchProps) {
        super(props);

        this.state = {
            cardPaymentState: null,
            ifcPaymentState: null,
            isSliderMoving: false,
            isValid: true,
            ifcCalculatorForm: new SofaForm<IfcCalculatorFormControls>(
                {
                    deposit: {
                        name: 'deposit',
                        defaultValue: '',
                        filters: [
                            maxLengthFilter(IFC_INPUT_MAX_LENGTH),
                            textFilter(NOT_NUMBER_REGEXP),
                        ],
                        ref: createRef<SofaInput>()
                    } as SofaFormControlDescription<string>,
                    months: {
                        name: 'months',
                        defaultValue: '',
                        filters: [
                            maxLengthFilter(IFC_INPUT_MAX_LENGTH),
                            textFilter(NOT_NUMBER_REGEXP),
                        ],
                        ref: createRef<SofaInput>()
                    } as SofaFormControlDescription<string>,
                },
                updateStateFunc => {
                    this.setState(updateStateFunc);
                },
                'ifcCalculatorForm',
                () => this.state,
                (error, source) => {
                    logsHelper.error(errorToBasketErrorModel(error, source));
                },
            )
        };
    }

    componentDidUpdate(prevProps: OwnProps & StateProps & DispatchProps, prevState: OwnState) {
        if (!this.state.isSliderMoving && !this.props.isPaymentUpdating && this.state.cardPaymentState !== this.props.updatedCardPaymentState) {
            this.setState({ cardPaymentState: this.props.updatedCardPaymentState });
        }

        if (!this.state.isSliderMoving && !this.props.isPaymentUpdating && this.state.ifcPaymentState !== this.props.updatedIfcPaymentState) {
            this.setState({ ifcPaymentState: this.props.updatedIfcPaymentState }, () => {
                this.state.ifcCalculatorForm.controls.deposit.setValue(this.props.updatedIfcPaymentState!.deposit.toString());
                this.state.ifcCalculatorForm.controls.months.setValue(this.props.updatedIfcPaymentState!.months.toString());
            });
        }

        if (!prevState.ifcPaymentState && this.state.ifcPaymentState) {
            this.state.ifcCalculatorForm.controls.months.setValue(this.state.ifcPaymentState.months.toString());
            this.state.ifcCalculatorForm.controls.deposit.setValue(this.state.ifcPaymentState.deposit.toString());
        }
    }

    handlePaymentMethodClick = (paymentType: PaymentTypes) => () => {
        if (this.props.paymentDetails?.paymentType !== paymentType) {
            if (paymentType === PaymentTypes.IFC) {
                if (this.isIfcAvailable()) {
                    this.props.changePaymentType(paymentType);
                    this.setState({ isValid: true, ifcPaymentState: this.props.updatedIfcPaymentState });
                }
            } else {
                this.props.changePaymentType(paymentType);
                this.setState({ isValid: true, cardPaymentState: this.props.updatedCardPaymentState });
            }
        }
    };

    handleCardPaymentUpdate = (prevValue: number, value: number) => {
        const cardPaymentState = this.state.cardPaymentState!;
        let updatedCardPaymentState = cardPaymentHelper.updatePayment(
            this.props.totalPrice,
            cardPaymentState,
            value);
        this.setState({ cardPaymentState: updatedCardPaymentState, isSliderMoving: true });
    };

    handleCardPaymentIncrement = (prevValue: number) => {
        let maxDeposit = this.state.cardPaymentState?.maxDeposit!;

        if (prevValue < maxDeposit) {
            const cardPaymentState = this.state.cardPaymentState!;
            let updatedCardPaymentState = cardPaymentHelper.incrementPayment(
                this.props.totalPrice,
                cardPaymentState);

            this.setState({ cardPaymentState: updatedCardPaymentState, isSliderMoving: true });
            this.updateCardPayment(updatedCardPaymentState);
        } else {
            this.props.addNotification('You do not need to pay more than the Total price for your Order', NotificationTypes.Err);
        }
    };

    handleCardPaymentDecrement = (prevValue: number) => {
        let minDeposit = this.state.cardPaymentState?.minDeposit!;

        if (prevValue > minDeposit) {
            const cardPaymentState = this.state.cardPaymentState!;
            let updatedCardPaymentState = cardPaymentHelper.decrementPayment(
                this.props.totalPrice,
                cardPaymentState);

            this.setState({ cardPaymentState: updatedCardPaymentState, isSliderMoving: true });
            this.updateCardPayment(updatedCardPaymentState);
        } else {
            this.props.addNotification(`You cannot pay less than ${formatCurrency(minDeposit)}.`, NotificationTypes.Err);
        }
    };

    handleCardPaymentChangeCommited = (value: number) => {
        const cardPaymentState = this.state.cardPaymentState!;
        let updatedCardPaymentState = cardPaymentHelper.updatePayment(
            this.props.totalPrice,
            cardPaymentState,
            value);
        this.updateCardPayment(updatedCardPaymentState);
        this.setState({ cardPaymentState: updatedCardPaymentState, isSliderMoving: false });
    };

    handleIfcMonthsUpdate = (prevValue: number, value: number) => {
        const ifcPaymentState = this.state.ifcPaymentState!;
        let updatedIfcPaymentState = ifcPaymentHelper.updateMonths(
            this.props.totalPrice,
            ifcPaymentState,
            value);
        this.setState({ ifcPaymentState: updatedIfcPaymentState, isSliderMoving: true });
        this.state.ifcCalculatorForm.controls.months.setValue(updatedIfcPaymentState.months.toString());
    };

    handleIfcMonthsIncrement = (prevValue: number) => {
        let maxMonths = this.state.ifcPaymentState!.maxMonths;

        if (prevValue < maxMonths) {
            const ifcPaymentState = this.state.ifcPaymentState!;
            let updatedIfcPaymentState = ifcPaymentHelper.incrementMonths(
                this.props.totalPrice,
                ifcPaymentState);

            this.setState({ ifcPaymentState: updatedIfcPaymentState, isSliderMoving: true });
            this.state.ifcCalculatorForm.controls.months.setValue(updatedIfcPaymentState.months.toString());
            this.updateIfcPayment(updatedIfcPaymentState);
        } else {
            this.props.addNotification(`You can pick a term only within ${this.state.ifcPaymentState!.ifcConfig.minFinanceMonths} - ${this.state.ifcPaymentState!.ifcConfig.maxFinanceMonths} months range.`, NotificationTypes.Err);
        }
    };

    handleIfcMonthsDecrement = (prevValue: number) => {
        let minMonths = this.state.ifcPaymentState!.minMonths;

        if (prevValue > minMonths) {
            const ifcPaymentState = this.state.ifcPaymentState!;
            let updatedIfcPaymentState = ifcPaymentHelper.decrementMonths(
                this.props.totalPrice,
                ifcPaymentState);

            this.setState({ ifcPaymentState: updatedIfcPaymentState, isSliderMoving: true });
            this.state.ifcCalculatorForm.controls.months.setValue(updatedIfcPaymentState.months.toString());
            this.updateIfcPayment(updatedIfcPaymentState);
        } else {
            this.props.addNotification(`You can pick a term only within ${this.state.ifcPaymentState!.ifcConfig.minFinanceMonths} - ${this.state.ifcPaymentState!.ifcConfig.maxFinanceMonths} months range.`, NotificationTypes.Err);
        }
    };

    handleIfcChangeCommited = () => {
        const ifcPaymentState = this.state.ifcPaymentState!;
        this.updateIfcPayment(ifcPaymentState);
        this.setState({ isSliderMoving: false });
    };

    handleIfcMonthsTextBoxOnBlur = (e: React.SyntheticEvent) => {
        let value = Number(this.state.ifcCalculatorForm.controls.months.value);

        const ifcPaymentState = this.state.ifcPaymentState!;
        let updatedIfcPaymentState = ifcPaymentHelper.updateMonths(
            this.props.totalPrice,
            ifcPaymentState,
            value);
        this.updateIfcPayment(updatedIfcPaymentState);
        this.setState({ ifcPaymentState: updatedIfcPaymentState });

        this.state.ifcCalculatorForm.controls.months.onBlur(e);
        this.state.ifcCalculatorForm.controls.months.setValue(updatedIfcPaymentState.months.toString());

        if (value !== updatedIfcPaymentState.months) {
            this.props.addNotification(`You can pick a term only within ${this.state.ifcPaymentState!.ifcConfig.minFinanceMonths} - ${this.state.ifcPaymentState!.ifcConfig.maxFinanceMonths} months range.`, NotificationTypes.Err);
        }
    };

    handleIfcDepositUpdate = (prevValue: number, value: number) => {
        const ifcPaymentState = this.state.ifcPaymentState!;
        let updatedIfcPaymentState = ifcPaymentHelper.updateDeposit(
            this.props.totalPrice,
            ifcPaymentState,
            value);
        this.setState({ ifcPaymentState: updatedIfcPaymentState, isSliderMoving: true });
        this.state.ifcCalculatorForm.controls.deposit.setValue(updatedIfcPaymentState.deposit.toString());
    };

    handleIfcDepositIncrement = (prevValue: number) => {
        let maxDeposit = this.state.ifcPaymentState!.maxDeposit;

        if (prevValue < maxDeposit) {
            const ifcPaymentState = this.state.ifcPaymentState!;
            let updatedIfcPaymentState = ifcPaymentHelper.incrementDeposit(
                this.props.totalPrice,
                ifcPaymentState);

            this.setState({ ifcPaymentState: updatedIfcPaymentState, isSliderMoving: true });
            this.state.ifcCalculatorForm.controls.deposit.setValue(updatedIfcPaymentState.deposit.toString());
            this.updateIfcPayment(updatedIfcPaymentState);
        } else {
            this.props.addNotification(`You do not need to pay more than ${formatCurrency(maxDeposit)} as a Deposit.`, NotificationTypes.Err);
        }
    };

    handleIfcDepositDecrement = (prevValue: number) => {
        let minDeposit = this.state.ifcPaymentState!.minDeposit;

        if (prevValue > minDeposit) {
            const ifcPaymentState = this.state.ifcPaymentState!;
            let updatedIfcPaymentState = ifcPaymentHelper.decrementDeposit(
                this.props.totalPrice,
                ifcPaymentState);

            this.setState({ ifcPaymentState: updatedIfcPaymentState, isSliderMoving: true });
            this.state.ifcCalculatorForm.controls.deposit.setValue(updatedIfcPaymentState.deposit.toString());
            this.updateIfcPayment(updatedIfcPaymentState);
        } else {
            this.props.addNotification(`You cannot pay less than ${formatCurrency(minDeposit)} as a Deposit.`, NotificationTypes.Err);
        }
    };

    handleIfcDepositTextBoxOnBlur = (e: React.SyntheticEvent) => {
        let value = Number(this.state.ifcCalculatorForm.controls.deposit.value);

        const ifcPaymentState = this.state.ifcPaymentState!;
        let updatedIfcPaymentState = ifcPaymentHelper.updateDeposit(
            this.props.totalPrice,
            ifcPaymentState,
            value);
        this.updateIfcPayment(updatedIfcPaymentState);
        this.setState({ ifcPaymentState: updatedIfcPaymentState });

        this.state.ifcCalculatorForm.controls.deposit.onBlur(e);
        this.state.ifcCalculatorForm.controls.deposit.setValue(updatedIfcPaymentState.deposit.toString());

        if (value < updatedIfcPaymentState.minDeposit) {
            this.props.addNotification(`You cannot pay less than ${formatCurrency(updatedIfcPaymentState.minDeposit)} as a Deposit.`, NotificationTypes.Err);
        }

        if (value > updatedIfcPaymentState.maxDeposit) {
            this.props.addNotification(`You do not need to pay more than ${formatCurrency(updatedIfcPaymentState.maxDeposit)} as a Deposit.`, NotificationTypes.Err);
        }
    };

    render(): JSX.Element {
        const { isPaymentCalculatorInitializing, paymentDetails, isPaymentTypeUpdating, basketLoadingItems, basketLoadingUpsells, isDonationLoading, isCalculatingDeliveryCost } = this.props;
        const { cardPaymentState, ifcPaymentState, isValid } = this.state;

        const isOrderTotalUpdating = basketLoadingItems.length > 0 || basketLoadingUpsells.length > 0 || isDonationLoading || isCalculatingDeliveryCost;

        return (
            <div className='basket-payment-type-selector' ref={this.element}>
                <div className='basket-payment-type-selector__title'>Choose your payment method *</div>
                <div className='basket-payment-type-selector__methods'>
                    {
                        (!isPaymentTypeUpdating && (isPaymentCalculatorInitializing || !cardPaymentState || !ifcPaymentState) || isOrderTotalUpdating)
                            ? <Loader show={true} overlay={true} />
                            : <>
                                {this.renderCardPaymentMethod(paymentDetails, cardPaymentState!)}
                                {this.renderIfcPaymentMethod(paymentDetails, ifcPaymentState!)}
                            </>
                    }
                </div>
                {!isValid && this.renderError()}
                <div className='basket-payment-type-selector__footer'>
                    {this.renderAdditionalMessages()}
                </div>
            </div>
        );
    }

    renderError(): JSX.Element {
        return (
            <div className='basket-payment-type-selector__error'>
                {PAYMENT_ERROR_MESSAGE}
            </div>
        );
    }

    renderCardPaymentMethod(paymentDetails: PaymentDetailsModel, paymentState: CardPaymentStateModel): JSX.Element {
        const { isValid } = this.state;
        const { paymentType } = paymentDetails;
        const { deposit, minDeposit, maxDeposit, amountLeft, isReadonlyMaxValue } = paymentState;
        const buttonText =
            <div>
                <div className='basket-payment-type-selector__method-title'>Debit, Credit card or PayPal</div>
                <div className='basket-payment-type-selector__method-description'>Make a payment securely by card or PayPal</div>
                {paymentType === PaymentTypes.Card && <div className='basket-payment-type-selector__method-body'>
                    <div className='basket-payment-type-selector__slider-header'>
                        <div className='basket-payment-type-selector__slider-header-title'>
                            Payment today
                        </div>
                        <div className='basket-payment-type-selector__slider-header-payment'>
                            {formatCurrency(deposit)}
                        </div>
                    </div>
                    {!isReadonlyMaxValue && this.renderCardPaymentEditor(deposit, minDeposit, maxDeposit, amountLeft)}
                </div>}
            </div>;

        return (
            <div className='basket-payment-type-selector__method'>
                <SofaButton
                    text={buttonText}
                    variant='choice'
                    onClick={this.handlePaymentMethodClick(PaymentTypes.Card)}
                    active={paymentType === PaymentTypes.Card}
                    addErrorClass={!isValid}>
                </SofaButton>
            </div>
        );
    }

    renderCardPaymentEditor(deposit: number, minDeposit: number, maxDeposit: number, amountLeft: number): JSX.Element {
        return (
            <>
                <SofaSlider
                    value={deposit}
                    minValue={minDeposit}
                    maxValue={maxDeposit}
                    onChange={this.handleCardPaymentUpdate}
                    onChangeCompleted={this.handleCardPaymentChangeCommited}
                    onPlusClick={this.handleCardPaymentIncrement}
                    onMinusClick={this.handleCardPaymentDecrement}
                />
                <div className='basket-payment-type-selector__slider-footer'>
                    <div className='basket-payment-type-selector__slider-footer-title'>
                        Balance payable before delivery,<br />online or in-store
                    </div>
                    <div className='basket-payment-type-selector__slider-footer-payment'>
                        {formatCurrency(amountLeft)}
                    </div>
                </div>
            </>
        );
    }

    renderIfcPaymentMethod(paymentDetails: PaymentDetailsModel, paymentState: IfcPaymentStateModel): JSX.Element {
        const { isValid } = this.state;
        const { paymentType } = paymentDetails;
        const { deposit, minDeposit, maxDeposit, monthlyPayment } = paymentState;
        const minDepositPercent = ifcPaymentHelper.getMinDepositPercent(paymentState);
        const buttonText =
            <div>
                <div className='basket-payment-type-selector__method-title'>Spread the cost, interest Free 0% APR</div>
                <div className='basket-payment-type-selector__method-description'>
                    {minDepositPercent > 0
                        ? <>Affordable monthly payments, {minDepositPercent}% deposit. Subject to acceptance.</>
                        : <>Affordable monthly payments with a deposit of your choosing. Subject to acceptance.</>
                    }
                </div>
                {paymentType === PaymentTypes.IFC &&
                    <div className='basket-payment-type-selector__method-body'>
                        <div className='basket-payment-type-selector__slider-header'>
                            <div className='basket-payment-type-selector__slider-header-title'>
                                Deposit
                            </div>
                            <div className='basket-payment-type-selector__slider-header-payment'>
                                <div className='basket-payment-type-selector__deposit-currency-char'>£</div>
                                <SofaInput
                                    label='Deposit'
                                    inputProps={{ inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', 'aria-label': 'Deposit' }}
                                    {...getPropsForSofaInput(this.state.ifcCalculatorForm.controls.deposit)}
                                    onBlur={this.handleIfcDepositTextBoxOnBlur}
                                />
                            </div>
                        </div>
                        <SofaSlider
                            value={deposit}
                            minValue={minDeposit}
                            maxValue={maxDeposit}
                            onChange={this.handleIfcDepositUpdate}
                            onChangeCompleted={this.handleIfcChangeCommited}
                            onPlusClick={this.handleIfcDepositIncrement}
                            onMinusClick={this.handleIfcDepositDecrement}
                        />
                        {this.renderRepaymentTerm(paymentState)}
                        <div className='basket-payment-type-selector__ifc-monthly-payment'>
                            <div>Monthly repayments</div><div>{formatCurrency(monthlyPayment)}</div>
                        </div>
                    </div>
                }
            </div>;

        return (
            <div className='basket-payment-type-selector__method'>
                <SofaButton
                    text={buttonText}
                    active={paymentType === PaymentTypes.IFC}
                    disabled={!this.isIfcAvailable()}
                    variant='choice'
                    onClick={this.handlePaymentMethodClick(PaymentTypes.IFC)}
                    addErrorClass={!isValid}>
                </SofaButton>
            </div>
        );
    }

    renderRepaymentTerm(paymentState: IfcPaymentStateModel): JSX.Element {
        const { minMonths, maxMonths, months } = paymentState;

        let isMonthsRangeExists = minMonths < maxMonths;

        return isMonthsRangeExists
            ? <>
                <div className='basket-payment-type-selector__slider-header'>
                    <div className='basket-payment-type-selector__slider-header-title'>
                        Repayment term
                        <div className='basket-payment-type-selector__slider-header-note'>
                            From {minMonths} - {maxMonths} months
                        </div>
                    </div>

                    <div className='basket-payment-type-selector__slider-header-payment'>
                        <SofaInput
                            label='Months'
                            inputProps={{ inputMode: 'decimal', pattern: '[0-9]*', autoCorrect: 'off', 'aria-label': 'Months' }}
                            {...getPropsForSofaInput(this.state.ifcCalculatorForm.controls.months)}
                            onBlur={this.handleIfcMonthsTextBoxOnBlur}
                        />
                    </div>
                </div>
                <SofaSlider
                    value={months}
                    minValue={minMonths}
                    maxValue={maxMonths}
                    onChange={this.handleIfcMonthsUpdate}
                    onChangeCompleted={this.handleIfcChangeCommited}
                    onPlusClick={this.handleIfcMonthsIncrement}
                    onMinusClick={this.handleIfcMonthsDecrement}
                />
            </>
            : <div className='basket-payment-type-selector__slider-header'>
                <div className='basket-payment-type-selector__slider-header-title'>
                    Repayment term
                    <div className='basket-payment-type-selector__slider-header-note'>
                        {minMonths} months
                    </div>
                </div>
            </div>;
    }

    renderAdditionalMessages(): JSX.Element {
        const { deliveryAddress, totalPrice, isIfcFormSubmitted, basket } = this.props;
        const { ifcPaymentState } = this.state;
        const isIfcAvailableByDelivery = ifcPaymentHelper.isIfcPaymentAvailableByDelivery(deliveryAddress);

        return (
            <>
                {ifcPaymentState && !isIfcAvailableByDelivery
                    && <div>Sorry, 0% interest Free credit is not currently available for customers in Eire.</div>}
                {ifcPaymentState && !ifcPaymentHelper.isIfcPaymentAvailableByPrice(totalPrice, ifcPaymentState) && isIfcAvailableByDelivery
                    && <div>Sorry, 0% interest Free Credit is only available on orders of at least {formatCurrency(ifcPaymentHelper.getMinBasketTotalForIfcPayment(ifcPaymentState))}.</div>}
                {isIfcFormSubmitted
                    && <div>For questions regarding 0% Interest Free Credit, call FREE on 0800 140 4040, we're open 8am - 11pm</div>}
                {basket.hasDwellProducts()
                    && <div>Sorry, 0% Interest Free Credit is not currently available to purchase Dwell products.</div>}
            </>
        );
    }

    updateIfcPayment = (ifcPaymentState: IfcPaymentStateModel) => {
        this.props.updateIfcPayment(ifcPaymentState);
        this.setState({ isSliderMoving: false });
    };

    updateCardPayment = (cardPaymentState: CardPaymentStateModel) => {
        this.setState({ isSliderMoving: false });
        this.props.updateCardPayment(cardPaymentState);
    };

    isIfcAvailable(): boolean {
        return Boolean(this.state.ifcPaymentState) && ifcPaymentHelper.isIfcPaymentAvailable(this.props.basket, this.props.isIfcFormSubmitted, this.state.ifcPaymentState!);
    }

    validate = (): boolean => {
        const isValid = this.props.paymentDetails && this.props.paymentDetails.paymentType !== PaymentTypes.None;
        this.setState({ isValid });

        return isValid;
    };

    scrollTo = () => {
        scrollHelper.scrollTo(this.element);
    };
}

function mapStateToProps(state: AppState): StateProps {
    return {
        isPaymentCalculatorInitializing: state.paymentState.isPaymentCalculatorInitializing,
        paymentDetails: state.paymentState.paymentDetails,
        updatedCardPaymentState: state.paymentState.cardPaymentState,
        updatedIfcPaymentState: state.paymentState.ifcPaymentState,
        totalPrice: state.basketState.basket!.totalPriceWithTax.value,
        isPaymentUpdating: state.paymentState.isPaymentUpdating,
        isPaymentTypeUpdating: state.paymentState.isPaymentTypeUpdating,
        deliveryAddress: state.basketState.basket!.deliveryAddress,
        isIfcFormSubmitted: state.paymentState.isIfcFormSubmitted,
        basket: state.basketState.basket!,
        basketLoadingItems: state.basketState.basketLoadingItems,
        basketLoadingUpsells: state.basketState.basketLoadingUpsells,
        isDonationLoading: state.basketState.isDonationLoading,
        isCalculatingDeliveryCost: state.basketState.isCalculatingDeliveryCost
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(
        dispatch,
        [
            changePaymentType,
            updateCardPayment,
            addNotification,
            updateIfcPayment
        ]
    );
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(BasketPaymentTypeSelector);
export type BasketPaymentTypeSelectorType = BasketPaymentTypeSelector;